import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { inventorySummaryApi, inventoryItemDtlApi, inventoryTransactionApi } from "../../api/api";

const SUMMARY_KEY = "inventorySummary";
const ITEM_KEY = "inventoryItems";
const TXN_KEY = "inventoryTransactions";

// ── Inventory Summary (stock counts per product) ────────────────

export function useInventorySummary() {
  return useQuery({
    queryKey: [SUMMARY_KEY],
    queryFn: inventorySummaryApi.getAll,
  });
}

export function useInventorySummaryItem(id) {
  return useQuery({
    queryKey: [SUMMARY_KEY, id],
    queryFn: () => inventorySummaryApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateInventorySummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventorySummaryApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: [SUMMARY_KEY] }),
  });
}

export function useUpdateInventorySummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventorySummaryApi.update,
    onSuccess: () => qc.invalidateQueries({ queryKey: [SUMMARY_KEY] }),
  });
}

export function useDeleteInventorySummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => inventorySummaryApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [SUMMARY_KEY] }),
  });
}

// ── Inventory Item DTL (individual tagged pieces) ───────────────

export function useInventoryItems() {
  return useQuery({
    queryKey: [ITEM_KEY],
    queryFn: inventoryItemDtlApi.getAll,
  });
}

export function useInventoryItem(id) {
  return useQuery({
    queryKey: [ITEM_KEY, id],
    queryFn: () => inventoryItemDtlApi.getById(id),
    enabled: !!id,
  });
}

export function useCreateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventoryItemDtlApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ITEM_KEY] });
      qc.invalidateQueries({ queryKey: [SUMMARY_KEY] });
    },
  });
}

export function useUpdateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: inventoryItemDtlApi.update,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ITEM_KEY] });
      qc.invalidateQueries({ queryKey: [SUMMARY_KEY] });
    },
  });
}

export function useDeleteInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => inventoryItemDtlApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [ITEM_KEY] });
      qc.invalidateQueries({ queryKey: [SUMMARY_KEY] });
    },
  });
}

// ── Inventory Transactions (ledger / movement history) ──────────

export function useInventoryTransactions() {
  return useQuery({
    queryKey: [TXN_KEY],
    queryFn: inventoryTransactionApi.getAll,
  });
}
